import { createSlice } from '@reduxjs/toolkit';
import * as AllMeta from '../../metadata';
import { metadataMap } from '../../metadata';

// Helper function to load queryOptions from localStorage
const loadQueryOptionsFromStorage = () => {
    try {
        const stored = localStorage.getItem('queryOptions');
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (error) {
        console.warn('Failed to load queryOptions from localStorage:', error);
    }
    return null;
};

// Helper function to save queryOptions to localStorage
const saveQueryOptionsToStorage = (queryOptions) => {
    try {
        localStorage.setItem('queryOptions', JSON.stringify(queryOptions));
    } catch (error) {
        console.warn('Failed to save queryOptions to localStorage:', error);
    }
};

const defaultQueryOptions = {
    patientCount: true,
    queryResults: true,
    medicationCount: false,
    procedureCount: false,
    encounterCount: false,
    labCount: false,
    top10Lists: false,
    distributionCharts: false,
    distributionMap: false,
};

const initialState = {
    enableAI: false,
    selectedDatabase: null,
    selectedMetadata: null,
    availableDatabases: [],
    queryOptions: { ...defaultQueryOptions, ...loadQueryOptionsFromStorage() },
};
const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        toggleAI(state) {
            state.enableAI = !state.enableAI;
        },
        setSelectedMetadata(state, action) {
            const databaseName = action.payload;

            // Extract the type from database name
            const dbType = databaseName.includes('-')
                ? databaseName.split('-')[1]
                : databaseName;

            // Try both uppercase and as-is
            const metadata = metadataMap[dbType.toUpperCase()] || metadataMap[dbType];

            if (metadata) {
                state.selectedMetadata = metadata;
            } else {
                console.warn(`No metadata found for database type: ${dbType}`);
                state.selectedMetadata = null;
            }
        },
        setSelectedDatabase(state, action) {
            state.selectedDatabase = action.payload;
        },
        setAvailableDatabases(state, action) {
            state.availableDatabases = action.payload;
        },
        clearDatabaseSelection(state) {
            state.selectedDatabase = null;
            state.selectedMetadata = null;
        },
        toggleQueryOption(state, action) {
            const option = action.payload;
            if (option in state.queryOptions) {
                state.queryOptions[option] = !state.queryOptions[option];
                // Save updated queryOptions to localStorage
                saveQueryOptionsToStorage(state.queryOptions);
            }
        }
    },
});

export const {
    toggleAI,
    setSelectedDatabase,
    setSelectedMetadata,
    setAvailableDatabases,
    clearDatabaseSelection,
    toggleQueryOption
} = settingsSlice.actions;

// Selectors
export const selectDatabase = (state) => state.settings.selectedDatabase;
export const selectMetadata = (state) => state.settings.selectedMetadata;
export const selectAvailableDatabases = (state) => state.settings.availableDatabases;
export const selectQueryOptions = (state) => state.settings.queryOptions;

export default settingsSlice.reducer;
